import React, { useState } from "react";
import GlobalWrapper from "../../components/Wrapper/GlobalWrapper";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  AvatarGroup,
  Tooltip,
} from "@mui/material";
import {
  CheckCircle,
  Flag,
  Assignment,
  TrendingUp,
  Groups,
  Speed,
  WorkOutline,
  TaskAlt,
  PlayArrow,
  HourglassEmpty,
  Cancel,
  Star,
  ErrorOutline,
  Person,
  BarChart,
  PieChart as PieChartIcon,
} from "@mui/icons-material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend,
  RadialBarChart,
  RadialBar,
} from "recharts";

// ─── Mock Data ───────────────────────────────────────────────────────────────

const manager = {
  name: "Deepak Kumar",
  avatar: "DK",
  role: "Engineering Manager",
  department: "Engineering",
};

// Section 1 stats
const stats = {
  taskCompletionRate: 78,
  highPriorityTasks: 5,
  projectsInProgress: 3,
  teamProgressOverview: 64,
};

// Section 2 - My Work Overview (donut)
const myWorkData = [
  { name: "Completed", value: 18, color: "#10B981" },
  { name: "In Progress", value: 7, color: "#3B82F6" },
  { name: "Pending", value: 4, color: "#F59E0B" },
  { name: "Cancelled", value: 1, color: "#EF4444" },
];
const totalMyTasks = myWorkData.reduce((a, b) => a + b.value, 0);

// Section 2 - My Performance (completion rates by week)
const myPerformanceData = [
  { week: "Week 1", completed: 5, assigned: 6 },
  { week: "Week 2", completed: 4, assigned: 5 },
  { week: "Week 3", completed: 6, assigned: 7 },
  { week: "Week 4", completed: 3, assigned: 5 },
];

// Section 3 - Team Leads
const teamLeads = [
  {
    id: 1,
    name: "Aarav Sharma",
    avatar: "AS",
    role: "Frontend Lead",
    tasksCompleted: 12,
    totalTasks: 15,
    performance: 80,
    color: "#6366F1",
  },
  {
    id: 2,
    name: "Priya Patel",
    avatar: "PP",
    role: "Design Lead",
    tasksCompleted: 9,
    totalTasks: 11,
    performance: 82,
    color: "#EC4899",
  },
  {
    id: 3,
    name: "Rahul Verma",
    avatar: "RV",
    role: "Backend Lead",
    tasksCompleted: 14,
    totalTasks: 18,
    performance: 78,
    color: "#F59E0B",
  },
  {
    id: 4,
    name: "Sneha Gupta",
    avatar: "SG",
    role: "QA Lead",
    tasksCompleted: 10,
    totalTasks: 12,
    performance: 83,
    color: "#10B981",
  },
  {
    id: 5,
    name: "Vikram Singh",
    avatar: "VS",
    role: "DevOps Lead",
    tasksCompleted: 7,
    totalTasks: 9,
    performance: 78,
    color: "#3B82F6",
  },
];

// Section 3 - All Tasks Status Overview
const allTasksStatus = [
  { status: "Completed", count: 52, color: "#10B981", icon: <TaskAlt sx={{ fontSize: 18 }} /> },
  { status: "In Progress", count: 23, color: "#3B82F6", icon: <PlayArrow sx={{ fontSize: 18 }} /> },
  { status: "Pending", count: 14, color: "#F59E0B", icon: <HourglassEmpty sx={{ fontSize: 18 }} /> },
  { status: "Overdue", count: 6, color: "#EF4444", icon: <ErrorOutline sx={{ fontSize: 18 }} /> },
  { status: "Cancelled", count: 3, color: "#94A3B8", icon: <Cancel sx={{ fontSize: 18 }} /> },
];
const totalAllTasks = allTasksStatus.reduce((a, b) => a + b.count, 0);

// ─── Stat Card Component ─────────────────────────────────────────────────────

const StatCard = ({ icon, title, value, gradient, subtitle, suffix }) => (
  <Card
    sx={{
      p: 0,
      borderRadius: 3,
      overflow: "hidden",
      border: "1px solid rgba(0,0,0,0.04)",
      transition: "all 0.3s ease",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 12px 32px rgba(0,0,0,0.08)",
      },
    }}
  >
    <Box sx={{ height: 4, background: gradient }} />
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="body2" sx={{ color: "#888", fontWeight: 500, mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#1a1a2e" }}>
            {value}
            {suffix && (
              <Typography component="span" variant="h6" sx={{ fontWeight: 600, color: "#aaa", ml: 0.5 }}>
                {suffix}
              </Typography>
            )}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: "#aaa", mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 2.5,
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ─── Custom Donut Center Label ───────────────────────────────────────────────

const DonutCenterLabel = ({ viewBox, value, label }) => {
  const { cx, cy } = viewBox;
  return (
    <g>
      <text x={cx} y={cy - 8} textAnchor="middle" dominantBaseline="central" style={{ fontSize: 28, fontWeight: 800, fill: "#1a1a2e" }}>
        {value}
      </text>
      <text x={cx} y={cy + 16} textAnchor="middle" dominantBaseline="central" style={{ fontSize: 12, fontWeight: 500, fill: "#999" }}>
        {label}
      </text>
    </g>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const ManagerDashboard = () => {
  return (
    <GlobalWrapper>
      <Box sx={{ width: "100%", minHeight: "100vh" }}>
        {/* ── Header ─────────────────────────────────────────────── */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
            borderRadius: 4,
            p: { xs: 3, sm: 4 },
            mb: 4,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -60,
              right: -60,
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: "rgba(99,102,241,0.12)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -40,
              left: "30%",
              width: 140,
              height: 140,
              borderRadius: "50%",
              background: "rgba(236,72,153,0.08)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "20%",
              right: "20%",
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: "rgba(245,158,11,0.06)",
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, position: "relative", zIndex: 1 }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: "#6366F1",
                fontWeight: 700,
                fontSize: 20,
                boxShadow: "0 4px 16px rgba(99,102,241,0.4)",
              }}
            >
              {manager.avatar}
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: "#fff", mb: 0.5 }}>
                Manager Dashboard
              </Typography>
              <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.6)" }}>
                {manager.name} &middot; {manager.role} &middot; {manager.department}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ═══════════════════════════════════════════════════════════
            SECTION 1: Stat Cards
        ═══════════════════════════════════════════════════════════ */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<Speed />}
              title="My Task Completion Rate"
              value={stats.taskCompletionRate}
              suffix="%"
              gradient="linear-gradient(135deg, #6366F1, #8B5CF6)"
              subtitle="This month"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<Flag />}
              title="High Priority Tasks"
              value={stats.highPriorityTasks}
              gradient="linear-gradient(135deg, #EF4444, #DC2626)"
              subtitle="Needs attention"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<WorkOutline />}
              title="Projects In Progress"
              value={stats.projectsInProgress}
              gradient="linear-gradient(135deg, #3B82F6, #2563EB)"
              subtitle="Active projects"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<Groups />}
              title="Team Progress Overview"
              value={stats.teamProgressOverview}
              suffix="%"
              gradient="linear-gradient(135deg, #10B981, #059669)"
              subtitle="Overall team"
            />
          </Grid>
        </Grid>

        {/* ═══════════════════════════════════════════════════════════
            SECTION 2: My Work Overview (Donut) + My Performance (Bar)
        ═══════════════════════════════════════════════════════════ */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* ── My Work Overview ─────────────────────────────────── */}
          <Grid item xs={12} md={5}>
            <Card
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.04)",
                overflow: "hidden",
                height: "100%",
                transition: "all 0.3s ease",
                "&:hover": { boxShadow: "0 8px 28px rgba(0,0,0,0.06)" },
              }}
            >
              <Box sx={{ height: 4, background: "linear-gradient(90deg, #6366F1, #EC4899)" }} />
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.5,
                      background: "linear-gradient(135deg, #6366F1, #8B5CF6)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                    }}
                  >
                    <PieChartIcon sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a2e" }}>
                    My Work Overview
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={myWorkData}
                        cx="50%"
                        cy="50%"
                        innerRadius={70}
                        outerRadius={100}
                        paddingAngle={4}
                        dataKey="value"
                        stroke="none"
                      >
                        {myWorkData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                        <DonutCenterLabel viewBox={{ cx: 150, cy: 120 }} value={totalMyTasks} label="Total Tasks" />
                      </Pie>
                      <ReTooltip
                        contentStyle={{
                          borderRadius: 12,
                          border: "none",
                          boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                          fontSize: 13,
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                {/* Legend */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center", mt: 1 }}>
                  {myWorkData.map((item) => (
                    <Box key={item.name} sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: item.color }} />
                      <Typography variant="caption" sx={{ color: "#666", fontWeight: 500 }}>
                        {item.name} ({item.value})
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* ── My Performance ───────────────────────────────────── */}
          <Grid item xs={12} md={7}>
            <Card
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.04)",
                overflow: "hidden",
                height: "100%",
                transition: "all 0.3s ease",
                "&:hover": { boxShadow: "0 8px 28px rgba(0,0,0,0.06)" },
              }}
            >
              <Box sx={{ height: 4, background: "linear-gradient(90deg, #3B82F6, #10B981)" }} />
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.5,
                      background: "linear-gradient(135deg, #3B82F6, #2563EB)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                    }}
                  >
                    <BarChart sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a2e" }}>
                    My Performance
                  </Typography>
                  <Chip
                    label="This Month"
                    size="small"
                    sx={{ ml: "auto", bgcolor: "rgba(59,130,246,0.08)", color: "#3B82F6", fontWeight: 600, fontSize: 11 }}
                  />
                </Box>

                <ResponsiveContainer width="100%" height={240}>
                  <ReBarChart data={myPerformanceData} barGap={8}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" />
                    <XAxis dataKey="week" tick={{ fontSize: 12, fill: "#999" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: "#999" }} axisLine={false} tickLine={false} />
                    <ReTooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: "none",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                        fontSize: 13,
                      }}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 12, fontWeight: 500 }}
                      iconType="circle"
                      iconSize={8}
                    />
                    <Bar dataKey="assigned" name="Assigned" fill="#3B82F6" radius={[6, 6, 0, 0]} barSize={28} />
                    <Bar dataKey="completed" name="Completed" fill="#10B981" radius={[6, 6, 0, 0]} barSize={28} />
                  </ReBarChart>
                </ResponsiveContainer>

                {/* Summary row */}
                <Box sx={{ display: "flex", gap: 3, mt: 2, justifyContent: "center" }}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#3B82F6" }}>
                      {myPerformanceData.reduce((a, b) => a + b.assigned, 0)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#999" }}>Total Assigned</Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#10B981" }}>
                      {myPerformanceData.reduce((a, b) => a + b.completed, 0)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#999" }}>Total Completed</Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#6366F1" }}>
                      {Math.round(
                        (myPerformanceData.reduce((a, b) => a + b.completed, 0) /
                          myPerformanceData.reduce((a, b) => a + b.assigned, 0)) *
                          100
                      )}
                      %
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#999" }}>Completion Rate</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ═══════════════════════════════════════════════════════════
            SECTION 3: Team Leads Overview + Tasks Overview
        ═══════════════════════════════════════════════════════════ */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* ── Team Leads Overview ──────────────────────────────── */}
          <Grid item xs={12} md={7}>
            <Card
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.04)",
                overflow: "hidden",
                height: "100%",
                transition: "all 0.3s ease",
                "&:hover": { boxShadow: "0 8px 28px rgba(0,0,0,0.06)" },
              }}
            >
              <Box sx={{ height: 4, background: "linear-gradient(90deg, #8B5CF6, #EC4899, #F59E0B)" }} />
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.5,
                      background: "linear-gradient(135deg, #8B5CF6, #6366F1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                    }}
                  >
                    <Groups sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a2e" }}>
                    Team Leads Overview
                  </Typography>
                  <Chip
                    label={`${teamLeads.length} Leads`}
                    size="small"
                    sx={{ ml: "auto", bgcolor: "rgba(139,92,246,0.08)", color: "#8B5CF6", fontWeight: 600, fontSize: 11 }}
                  />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {teamLeads.map((lead) => (
                    <Box
                      key={lead.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 2,
                        borderRadius: 2.5,
                        bgcolor: "rgba(0,0,0,0.01)",
                        border: "1px solid rgba(0,0,0,0.03)",
                        transition: "all 0.25s ease",
                        "&:hover": {
                          bgcolor: `${lead.color}06`,
                          borderColor: `${lead.color}20`,
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,
                          bgcolor: lead.color,
                          fontWeight: 700,
                          fontSize: 14,
                          boxShadow: `0 4px 12px ${lead.color}30`,
                        }}
                      >
                        {lead.avatar}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: "#1a1a2e" }}>
                              {lead.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#999" }}>
                              {lead.role}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: "right" }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: lead.color }}>
                              {lead.tasksCompleted}/{lead.totalTasks}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#bbb" }}>
                              tasks done
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={lead.performance}
                            sx={{
                              flex: 1,
                              height: 6,
                              borderRadius: 3,
                              bgcolor: "rgba(0,0,0,0.04)",
                              "& .MuiLinearProgress-bar": {
                                borderRadius: 3,
                                bgcolor: lead.color,
                              },
                            }}
                          />
                          <Typography variant="caption" sx={{ fontWeight: 700, color: lead.color, minWidth: 35 }}>
                            {lead.performance}%
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* ── Tasks Overview (All Tasks Current Status) ─────────── */}
          <Grid item xs={12} md={5}>
            <Card
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.04)",
                overflow: "hidden",
                height: "100%",
                transition: "all 0.3s ease",
                "&:hover": { boxShadow: "0 8px 28px rgba(0,0,0,0.06)" },
              }}
            >
              <Box sx={{ height: 4, background: "linear-gradient(90deg, #10B981, #3B82F6, #F59E0B)" }} />
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.5,
                      background: "linear-gradient(135deg, #10B981, #059669)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                    }}
                  >
                    <Assignment sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a2e" }}>
                    Tasks Overview
                  </Typography>
                </Box>

                {/* Total */}
                <Box
                  sx={{
                    textAlign: "center",
                    mb: 3,
                    p: 2.5,
                    borderRadius: 3,
                    background: "linear-gradient(135deg, rgba(99,102,241,0.04), rgba(236,72,153,0.04))",
                    border: "1px solid rgba(99,102,241,0.06)",
                  }}
                >
                  <Typography variant="h3" sx={{ fontWeight: 800, color: "#1a1a2e" }}>
                    {totalAllTasks}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#999", fontWeight: 500 }}>
                    Total Tasks Across Team
                  </Typography>
                </Box>

                {/* Status Breakdown */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {allTasksStatus.map((item) => {
                    const percentage = Math.round((item.count / totalAllTasks) * 100);
                    return (
                      <Box key={item.status}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: 1.5,
                                bgcolor: `${item.color}12`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: item.color,
                              }}
                            >
                              {item.icon}
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#555" }}>
                              {item.status}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: item.color }}>
                              {item.count}
                            </Typography>
                            <Chip
                              label={`${percentage}%`}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: 10,
                                fontWeight: 700,
                                bgcolor: `${item.color}10`,
                                color: item.color,
                              }}
                            />
                          </Box>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: "rgba(0,0,0,0.03)",
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 4,
                              bgcolor: item.color,
                              transition: "width 0.8s ease",
                            },
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>

                {/* Completion summary */}
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "rgba(16,185,129,0.04)",
                    border: "1px solid rgba(16,185,129,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TrendingUp sx={{ fontSize: 18, color: "#10B981" }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#555" }}>
                      Overall Completion
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#10B981" }}>
                    {Math.round((allTasksStatus[0].count / totalAllTasks) * 100)}%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </GlobalWrapper>
  );
};

export default ManagerDashboard;
